import type { FormatterPreset } from "@imminently/decisively-core";
import { Extension } from "@tiptap/core";
import { Placeholder } from "@tiptap/extension-placeholder";
import { type Editor, useEditor } from "@tiptap/react";
import clsx from "clsx";
import React from "react";
import Button from "../../common/Button/Button";
import Icon from "../../common/Icon/Icon";
import RadioButton from "../../form/Radio/RadioButton";
import RadioGroup from "../../form/Radio/RadioGroup";
import RichTextEditor from "../../form/RichTextEditor/RichTextEditor";
import { AttributeInsertControl } from "./AttributeInsertControl";
import styles from "./AttributeRichTextEditor.module.scss";
import { AttributeTokenProvider } from "./tiptap/AttributeTokenContext";
import { AttributeSuggestion } from "./tiptap/attributeSuggestion";
import { attributeTemplateExtensions, countMigratedTokens } from "./tiptap/extensions";
import { htmlToTemplate, looksLikeHtml, plainTemplateToHtml, templateToHtml } from "./tiptap/templateHtml";
import type { AttributeOption } from "./types";

export { AttributeInsertControl } from "./AttributeInsertControl";
export type { AttributeInsertControlProps } from "./AttributeInsertControl";
export { AttributeToken, attributeTokenContent, attributeTokenTemplate } from "./tiptap/attributeToken";
export type { AttributeTokenAttrs } from "./tiptap/attributeToken";
export { AttributeTokenProvider, useAttributeTokenContext } from "./tiptap/AttributeTokenContext";
export type { AttributeTokenContextValue } from "./tiptap/AttributeTokenContext";
export { AttributeSuggestion } from "./tiptap/attributeSuggestion";
export type { AttributeSuggestionOptions } from "./tiptap/attributeSuggestion";
export { htmlToTemplate, looksLikeHtml, plainTemplateToHtml, templateToHtml } from "./tiptap/templateHtml";
export { attributeTemplateExtensions, countMigratedTokens } from "./tiptap/extensions";

/**
 * Template editor with inline attribute chips (ENG-591). Replaces the old
 * `AttributeTemplateEditor`.
 *
 * The deci-ui `RichTextEditor`, plus:
 *   - a right-aligned "+ Insert attribute" button (`AttributeInsertControl`)
 *     and a Friendly / Raw switch at the end of the toolbar, after the
 *     formatting controls (Turn into, lists, colours, marks, alignment, link);
 *   - an inline `attributeToken` node that renders the shared chip in the text;
 *     clicking a chip reopens the picker to re-point it or set a formatter
 *     (see `AttributeTokenView`);
 *   - `@` quick-insert via `@tiptap/suggestion` (`AttributeSuggestion`), a
 *     compact inline list you type to filter and arrow through. `/` is left
 *     free for a future command menu.
 *
 * `format` picks what the template is:
 *   - `"html"` (default): formatted text. The template is the HTML with each chip
 *     written as `{{ key | formatter }}`, and the formatting toolbar shows.
 *   - `"text"`: plain text, e.g. an email subject or an SMS. No formatting
 *     toolbar; the template is the text with `{{ }}` tokens, one line per paragraph.
 * A stored value in the other format is converted on load, and saved in this
 * format on the next edit.
 *
 * Raw shows the template and is editable; switching back to Friendly re-renders
 * the tokens as chips. The formatting controls and Insert are disabled while Raw
 * is showing.
 *
 * `compact` drops the toolbar for small fields: attributes go in with `@`, and
 * Friendly / Raw is one toggle button in the corner that appears on hover.
 *
 * A deprecated `{{ @parent/.../name }}` token in the default value is
 * auto-migrated to the `name` attribute: its chip shows amber, an amber notice
 * under the content says it needs saving, and `onChange` fires once on load
 * with the migrated template so a form sees the change and can save it.
 *
 * To build a different toolbar, compose `RichTextEditor` directly with the
 * exported `AttributeToken`, `AttributeSuggestion`, `AttributeTokenProvider`
 * and `AttributeInsertControl`.
 */

export interface AttributeRichTextValue {
  /** Editor HTML, with `attributeToken` spans. */
  html: string;
  /** Plain text, one line per paragraph, with each token as `{{ }}`. Formatting is lost. */
  text: string;
  /** The template to store: `text` for the `"text"` format, else the HTML with each token as `{{ }}`. */
  template: string;
}

export type AttributeRichTextFormat = "html" | "text";

export interface AttributeRichTextEditorProps {
  attributes: AttributeOption[];
  groupOrder?: string[];
  formatterOptionsFor?: (attribute: AttributeOption) => FormatterPreset[];
  /** `"html"` for formatted text, `"text"` for plain text. Default `"html"`. See the component docs. */
  format?: AttributeRichTextFormat;
  /** Blocks new lines (Enter does nothing). For one-line fields such as a subject. */
  singleLine?: boolean;
  /**
   * Initial template: HTML or plain text, with `{{ }}` tokens. Known tokens become
   * chips. The editor owns its content after mount.
   */
  defaultValue?: string;
  /** Fires on every change, in Friendly or Raw, with all three serialisations. */
  onChange?: (value: AttributeRichTextValue) => void;
  /** Offers the Friendly / Raw switch. Default true. */
  showRawView?: boolean;
  /** Offers the editor-only "Display as" short label on chips (`as "label"`). Renderers ignore it. Default false. */
  allowCustomLabel?: boolean;
  /**
   * No toolbar: attributes go in with `@`, and Friendly / Raw becomes one toggle
   * button in the corner that shows on hover or focus. For small fields such as a subject.
   */
  compact?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

type Mode = "friendly" | "raw";

const toValue = (editor: Editor, format: AttributeRichTextFormat): AttributeRichTextValue => {
  const html = editor.getHTML();
  const text = editor.getText({ blockSeparator: "\n" });
  return { html, text, template: format === "text" ? text : htmlToTemplate(html) };
};

/** Swallows Enter so the editor stays on one line. */
const SingleLine = Extension.create({
  name: "singleLine",
  addKeyboardShortcuts() {
    return { Enter: () => true, "Shift-Enter": () => true };
  },
});

export const AttributeRichTextEditor = ({
  attributes,
  groupOrder,
  formatterOptionsFor,
  format = "html",
  singleLine = false,
  defaultValue = "",
  onChange,
  showRawView = true,
  allowCustomLabel = false,
  compact = false,
  disabled = false,
  placeholder = compact ? "Type @ to insert an attribute" : "Start typing…",
  className,
}: AttributeRichTextEditorProps): React.ReactElement => {
  const [mode, setMode] = React.useState<Mode>("friendly");
  const [rawDraft, setRawDraft] = React.useState("");

  // Template -> editor HTML. A value is read as HTML if it has markup, otherwise as
  // plain text (one paragraph per line), whichever format this editor stores.
  const toHtml = React.useCallback(
    (template: string, list: AttributeOption[]) =>
      looksLikeHtml(template) ? templateToHtml(template, list) : plainTemplateToHtml(template, list),
    [],
  );
  // Parsed once: the editor owns its content after mount.
  const [initialContent] = React.useState(() => toHtml(defaultValue, attributes));

  // The suggestion plugin reads this on every keystroke, so it always sees the
  // current list without the editor being re-created when the list changes.
  const attributesRef = React.useRef(attributes);
  attributesRef.current = attributes;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    extensions: [
      ...attributeTemplateExtensions(),
      Placeholder.configure({ placeholder }),
      AttributeSuggestion.configure({
        chars: ["@"],
        getItems: () => attributesRef.current,
      }),
      ...(singleLine ? [SingleLine] : []),
    ],
    content: initialContent,
    onUpdate: ({ editor: current }) => onChangeRef.current?.(toValue(current, format)),
  });

  // No update event: toggling editability isn't a content change, and emitting one
  // would report the untouched value on mount and mark a form dirty just by opening it.
  React.useEffect(() => {
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);

  // Deprecated `@parent/...` tokens were migrated while parsing the default value.
  // Report the migrated template once, so the owning form is dirty and saving completes it.
  // Only the content the editor was created with is migrated, so this runs once per editor.
  const [migratedCount, setMigratedCount] = React.useState(0);
  React.useEffect(() => {
    if (!editor) return;
    const count = countMigratedTokens(editor);
    setMigratedCount(count);
    if (count > 0) onChangeRef.current?.(toValue(editor, format));
  }, [editor]);

  const tokenContext = React.useMemo(
    () => ({ attributes, groupOrder: groupOrder ?? [], formatterOptionsFor, allowCustomLabel }),
    [attributes, groupOrder, formatterOptionsFor, allowCustomLabel],
  );

  const changeMode = (next: string) => {
    if (next === mode || !editor) return;
    if (next === "raw") setRawDraft(toValue(editor, format).template);
    setMode(next as Mode);
  };

  // Raw edits go straight into the (hidden) editor, so Friendly is always in
  // sync and `onChange` reports the same shape in either view.
  const changeRaw = (text: string) => {
    setRawDraft(text);
    const html =
      format === "text"
        ? plainTemplateToHtml(text, attributesRef.current)
        : templateToHtml(text, attributesRef.current);
    editor?.commands.setContent(html, { emitUpdate: true });
  };

  return (
    <AttributeTokenProvider value={tokenContext}>
      <RichTextEditor
        editor={editor}
        disabled={disabled || mode === "raw"}
        className={clsx(compact && styles.compact, className)}
      >
        {compact ? null : (
          <RichTextEditor.Toolbar>
            {format === "html" ? (
              <RichTextEditor.Group>
                <RichTextEditor.TurnInto />
              </RichTextEditor.Group>
            ) : null}
            {format === "html" ? (
              <RichTextEditor.Group>
                <RichTextEditor.Color />
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
                <RichTextEditor.Strikethrough />
              </RichTextEditor.Group>
            ) : null}
            {format === "html" ? (
              <RichTextEditor.Group>
                <RichTextEditor.Link />
                <RichTextEditor.Align />
                <RichTextEditor.Lists />
              </RichTextEditor.Group>
            ) : null}
            <RichTextEditor.FillSpace />
            <RichTextEditor.Group className={styles.endGroup}>
              <AttributeInsertControl
                attributes={attributes}
                groupOrder={groupOrder}
              />
              {showRawView ? (
                <RadioGroup
                  ariaLabel="Editor view"
                  variant="buttons"
                  value={mode}
                  onValueChange={changeMode}
                  className={styles.modeSwitch}
                >
                  <RadioButton
                    value="friendly"
                    aria-label="Friendly"
                    tooltip="Friendly"
                    iconOnly
                  >
                    <Icon
                      name="square-text"
                      size={16}
                      aria-hidden
                    />
                  </RadioButton>
                  <RadioButton
                    value="raw"
                    aria-label="Raw"
                    tooltip="Raw"
                    iconOnly
                  >
                    <Icon
                      name="code-xml"
                      size={16}
                      aria-hidden
                    />
                  </RadioButton>
                </RadioGroup>
              ) : null}
            </RichTextEditor.Group>
          </RichTextEditor.Toolbar>
        )}

        {compact && showRawView ? (
          <Button
            icon
            size="sm"
            label={mode === "raw" ? "Show friendly view" : "Show raw template"}
            selected={mode === "raw"}
            aria-pressed={mode === "raw"}
            className={styles.compactToggle}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => changeMode(mode === "raw" ? "friendly" : "raw")}
          >
            <Icon name="code-xml" />
          </Button>
        ) : null}

        {/* Kept mounted in Raw so the editor view (and its undo history) survives the switch. */}
        <RichTextEditor.Content className={clsx(mode === "raw" && styles.hidden, singleLine && styles.singleLine)} />
        {mode === "raw" ? (
          <textarea
            className={clsx(styles.raw, singleLine && styles.rawSingleLine)}
            value={rawDraft}
            onChange={(event) => changeRaw(singleLine ? event.target.value.replace(/\n/g, " ") : event.target.value)}
            readOnly={disabled}
            spellCheck={false}
            rows={singleLine ? 1 : undefined}
            aria-label="Raw template"
          />
        ) : null}
        {migratedCount > 0 ? (
          <output className={styles.migrationNotice}>
            <Icon
              name="warning"
              size={14}
              aria-hidden
            />
            <span>
              {migratedCount === 1
                ? "1 deprecated @parent reference was auto-migrated to its attribute."
                : `${migratedCount} deprecated @parent references were auto-migrated to their attributes.`}{" "}
              Save to keep the change.
            </span>
          </output>
        ) : null}
      </RichTextEditor>
    </AttributeTokenProvider>
  );
};

export default AttributeRichTextEditor;
